import { useMemo } from 'react';
import useCanvasStore from '../pages/Canvas/useCanvasStore';
import { getFormStepStore } from '../FormStep/useFormStepStore';
import { getDynamicFormStore } from '../DynamicFormBuilder/useDynamicFormStore';
import { getDynamicReviewTaskStore } from '../DynamicReviewTask/useDynamicReviewTaskStore';

const FORM_LIKE_TYPES = new Set(['form_step', 'dynamic_form', 'dynamic_review_task']);

function storeGetterFor(type) {
  if (type === 'form_step') return getFormStepStore;
  if (type === 'dynamic_form') return getDynamicFormStore;
  if (type === 'dynamic_review_task') return getDynamicReviewTaskStore;
  return null;
}

/**
 * Walks the canvas graph upstream from {@code componentId} and collects every reachable
 * v2 form-like (form_step / dynamic_form / dynamic_review_task) that is itself a flow entry —
 * i.e. its componentData has {@code flowEntryId === componentId} AND a non-null
 * {@code flowConfig}. The FlowSettingsBlock "attach to an existing flow" picker consumes this.
 *
 * Client-side mirror of the server-side {@code FlowRouter.listMembers} direction, inverted:
 * server walks downstream from the entry; this walks upstream from the current component.
 *
 * @param {string} componentId — the component the user is currently editing.
 * @returns {Array<{componentId: string, componentName: string, flowTitle: string}>}
 */
export default function useUpstreamFlowEntries(componentId) {
  const nodes = useCanvasStore((s) => s.nodes);
  const edges = useCanvasStore((s) => s.edges);

  return useMemo(() => {
    if (!componentId || !nodes || !edges) return [];

    // Build reverse-adjacency index so upstream BFS is O(V+E).
    const reverseAdj = new Map();
    for (const e of edges) {
      if (!e || !e.source || !e.target) continue;
      if (!reverseAdj.has(e.target)) reverseAdj.set(e.target, []);
      reverseAdj.get(e.target).push(e.source);
    }

    const visited = new Set();
    const queue = [componentId];
    const upstreamIds = new Set();
    while (queue.length > 0) {
      const current = queue.shift();
      if (visited.has(current)) continue;
      visited.add(current);
      const parents = reverseAdj.get(current) || [];
      for (const p of parents) {
        if (!visited.has(p)) {
          upstreamIds.add(p);
          queue.push(p);
        }
      }
    }

    const nodeById = new Map();
    for (const n of nodes) {
      if (n && n.id) nodeById.set(n.id, n);
    }

    const results = [];
    const seen = new Set();
    for (const id of upstreamIds) {
      if (seen.has(id)) continue;
      const node = nodeById.get(id);
      if (!node) continue;
      const type = node.data?.type || node.type;
      if (!FORM_LIKE_TYPES.has(type)) continue;

      // Prefer the store's live data (which reflects unsaved in-memory edits). Fall back to
      // whatever was hydrated onto the node on canvas load so upstream entries still show up
      // when the user hasn't opened that node's config panel in this session — otherwise
      // users would see an empty "existing flows" picker for flows that clearly exist.
      let data = null;
      let componentName = node.data?.name || id;
      const getStore = storeGetterFor(type);
      if (getStore) {
        const state = getStore(id).getState();
        if (state?.loaded) {
          data = state.data || null;
          componentName = state.name || componentName;
        }
      }
      if (!data) {
        data = node.data?.componentData || null;
      }
      if (!data) continue;

      const isEntry = data.flowEntryId && data.flowEntryId === id && data.flowConfig;
      if (!isEntry) continue;

      seen.add(id);
      results.push({
        componentId: id,
        componentName,
        flowTitle: data.flowConfig?.title || '',
      });
    }

    return results;
  }, [componentId, nodes, edges]);
}
